/**
 * Scheduled reports (V1-E10-T05): the tenant's numbers mailed daily or weekly, with the calls as CSV. Anyone who
 * reads calls sees them; making, sending or removing one takes settings:write (it mails call data out).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Report = Schemas["ReportOut"];
const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";
const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export function Reports() {
  const t = useT();
  const id = useId();
  const format = useFormat();
  const canEdit = useCan("settings:write");
  const reports = useApiQuery("reports", (api) => api.GET("/v1/reports"));
  return (
    <section aria-labelledby={`${id}-title`} className="space-y-3">
      <h2 id={`${id}-title`} className="text-lg font-semibold">{t("reports.title")}</h2>
      <p className="text-sm text-[var(--color-muted)]">{t("reports.intro")}</p>
      {reports.isError && <ErrorState error={reports.error} />}
      {reports.data && reports.data.length > 0 && (
        <Table
          caption={t("reports.title")}
          rows={reports.data}
          rowKey={(r) => r.id}
          columns={[
            { header: t("reports.col.name"), cell: (r) => r.name },
            { header: t("reports.col.when"), cell: (r) => <When report={r} /> },
            { header: t("reports.col.to"), cell: (r) => r.emails.join(", ") },
            { header: t("reports.col.last"), cell: (r) => (r.last_sent_at ? format.dateTime(r.last_sent_at) : "—") },
            ...(canEdit ? [{ header: "", cell: (r: Report) => <Actions report={r} /> }] : []),
          ]}
        />
      )}
      {canEdit && <NewReport />}
    </section>
  );
}

function When({ report }: { report: Report }) {
  const t = useT();
  const hour = `${String(report.hour).padStart(2, "0")}:00`;
  return (
    <span>
      {report.every === "weekly"
        ? t("reports.weeklyAt", { day: t(`reports.day.${report.weekday ?? 0}` as MessageId), hour })
        : t("reports.dailyAt", { hour })}
      {!report.enabled && ` (${t("reports.off")})`}
    </span>
  );
}

function Actions({ report }: { report: Report }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const path = { params: { path: { report_id: report.id } } };
  const send = useMutation({ mutationFn: () => data(api.POST("/v1/reports/{report_id}/send", path)) });
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/reports/{report_id}", path)),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["reports", tenant] }),
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="ghost" disabled={send.isPending} onClick={() => send.mutate()}
        aria-label={t("reports.sendFor", { name: report.name })}>{t("reports.send")}</Button>
      <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate()}
        aria-label={t("reports.removeFor", { name: report.name })}>{t("reports.remove")}</Button>
      {send.data && <span role="status" className="text-xs">{t(send.data.delivered ? "reports.sent" : "reports.notSent")}</span>}
      {(send.isError || remove.isError) && <ErrorState error={send.error ?? remove.error} />}
    </div>
  );
}

function NewReport() {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const [name, setName] = useState("");
  const [every, setEvery] = useState<"daily" | "weekly">("daily");
  const [weekday, setWeekday] = useState(0);
  const [hour, setHour] = useState(8);
  const [emails, setEmails] = useState("");
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/reports", { body: {
      name: name.trim(), every, hour, weekday: every === "weekly" ? weekday : null, enabled: true,
      emails: emails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean),
    } })),
    onSuccess: async () => {
      setName("");
      setEmails("");
      await queries.invalidateQueries({ queryKey: ["reports", tenant] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && emails.trim()) create.mutate();
  };
  return (
    <form onSubmit={submit} aria-label={t("reports.new")} className="flex flex-wrap items-end gap-3">
      <label htmlFor={`${id}-name`} className="flex flex-col gap-1 text-sm">
        {t("reports.col.name")}
        <input id={`${id}-name`} className={field} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </label>
      <label htmlFor={`${id}-every`} className="flex flex-col gap-1 text-sm">
        {t("reports.every")}
        <select id={`${id}-every`} className={field} value={every} onChange={(e) => setEvery(e.target.value as "daily" | "weekly")}>
          <option value="daily">{t("reports.daily")}</option>
          <option value="weekly">{t("reports.weekly")}</option>
        </select>
      </label>
      {every === "weekly" && (
        <label htmlFor={`${id}-day`} className="flex flex-col gap-1 text-sm">
          {t("reports.dayLabel")}
          <select id={`${id}-day`} className={field} value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
            {DAYS.map((d) => <option key={d} value={d}>{t(`reports.day.${d}` as MessageId)}</option>)}
          </select>
        </label>
      )}
      <label htmlFor={`${id}-hour`} className="flex flex-col gap-1 text-sm">
        {t("reports.hour")}
        <input id={`${id}-hour`} type="number" min={0} max={23} className={field} value={hour}
          onChange={(e) => setHour(Number(e.target.value))} />
      </label>
      <label htmlFor={`${id}-emails`} className="flex min-w-64 flex-col gap-1 text-sm">
        {t("reports.emails")}
        <input id={`${id}-emails`} className={field} value={emails} placeholder="ops@example.com, …"
          onChange={(e) => setEmails(e.target.value)} />
      </label>
      <Button type="submit" disabled={create.isPending || !name.trim() || !emails.trim()}>{t("reports.add")}</Button>
      {create.isError && <div className="w-full"><ErrorState error={create.error} /></div>}
    </form>
  );
}
